import axios from 'axios';
import rison from 'rison';
import { webalize } from 'webalize';
import * as Sentry from '@sentry/react';

import { API_ROOT, setAuthHeader } from './config';

const endpoint = `${API_ROOT}/file-manager`;

export default class FilesService {
  static async load(path, allowedExtensions) {
    if (allowedExtensions) {
      allowedExtensions = rison.encode(allowedExtensions);
    }

    return axios.get(`${endpoint}/files`, { params: { path, allowedExtensions }, headers: setAuthHeader() });
  }

  static async getReclamationFiles(path) {
    return axios.get(`${endpoint}/files`, { params: { path }, headers: setAuthHeader() });
  }

  static upload(files, path) {
    const formData = new FormData();

    formData.append('path', path);

    files.forEach((file) => {
      formData.append('files', file, file.name);
    });

    return axios.post(`${endpoint}/upload`, formData);
  }

  static async createFolderAndUpload(files, folder) {
    try {
      // Breadcrumb before creating folder
      Sentry.addBreadcrumb({
        category: 'file_upload',
        message: 'Creating folder for reclamation',
        level: 'info',
        data: {
          folder: folder,
          path: `reklamace/${folder}`
        }
      });

      await this.createFolder('reklamace', folder);

      // Breadcrumb after folder creation
      Sentry.addBreadcrumb({
        category: 'file_upload',
        message: 'Folder created successfully',
        level: 'info',
        data: {
          folder: folder,
          path: `reklamace/${folder}`
        }
      });

      const formData = new FormData();
      formData.append('path', `reklamace/${folder}/`);

      // Track each file being added to FormData
      files.forEach((file, index) => {
        formData.append('files', file, file.name);

        Sentry.addBreadcrumb({
          category: 'file_upload',
          message: `File ${index + 1} added to upload queue`,
          level: 'info',
          data: {
            folder: folder,
            file_index: index,
            file_name: file.name,
            file_size: file.size,
            file_type: file.type
          }
        });
      });

      // Breadcrumb before upload request
      Sentry.addBreadcrumb({
        category: 'file_upload',
        message: 'Sending upload request to backend',
        level: 'info',
        data: {
          folder: folder,
          path: `reklamace/${folder}/`,
          files_count: files.length,
          total_size: files.reduce((sum, file) => sum + (file.size || 0), 0)
        }
      });

      const response = await axios.post(`${endpoint}/upload`, formData);

      // Breadcrumb after successful upload
      Sentry.addBreadcrumb({
        category: 'file_upload',
        message: 'Upload completed successfully',
        level: 'info',
        data: {
          folder: folder,
          response_status: response.status,
          response_data: JSON.stringify(response.data),
          response_headers: JSON.stringify(response.headers),
          files_count: files.length
        }
      });

      return response;
    } catch (error) {
      // Breadcrumb for upload failure
      Sentry.addBreadcrumb({
        category: 'file_upload',
        message: 'Upload failed',
        level: 'error',
        data: {
          folder: folder,
          error_message: error.message,
          error_code: error.code,
          response_status: error.response?.status,
          error_response_data: error.response?.data ? JSON.stringify(error.response.data) : null,
          error_stack: error.stack
        }
      });

      // Re-throw the error to maintain existing error handling
      throw error;
    }
  }

  static createFolder(path, name) {
    return axios.post(`${endpoint}/folder`,
      { params: { path, name: webalize(name) } }
    );
  }

  static remove(path) {
    return axios.delete(`${endpoint}/files`, { params: { path: encodeURI(path) }, headers: setAuthHeader() });
  }
}
